// 表单文本与接口取值之间的换算：多行文本与数组、金额与微单位、北京时间输入与 RFC 3339 UTC、JSON 文本。
// 换算失败返回 null，由表单显示字段错误；不抛异常。
import { toBeijing, TZ_OFFSET } from "@/ui";

/** 币种是三位大写的 ISO 4217 代码；错误文案四个表单共用。 */
export const CURRENCY_ERROR = "三位大写币种代码，例如 USD。";
export function validCurrency(code: string): boolean {
  return /^[A-Z]{3}$/.test(code);
}

/** MCC 是三位数字（与后端 model.Operator 同一口径，#405、#440）。只查格式，留空的含义由各表单决定。 */
export function validMcc(mcc: string): boolean {
  return /^\d{3}$/.test(mcc);
}

/** MNC 是两到三位数字，前导零有意义，按字符串保留（#405、#440）。只查格式，留空表示全部运营商还是必填由各表单决定。 */
export function validMnc(mnc: string): boolean {
  return /^\d{2,3}$/.test(mnc);
}

/** 每行一个值，忽略空行与首尾空白。 */
export function splitLines(text: string): string[] {
  return text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
}

export function joinLines(items: string[] | null | undefined): string {
  return (items ?? []).join("\n");
}

/** 正整数文本转数字，其他返回 null。 */
export function parsePositive(text: string): number | null {
  const n = Number(text);
  return /^\d+$/.test(text.trim()) && n > 0 ? n : null;
}

/** 在 [min, max] 之内的整数文本转数字，其他返回 null。表单里的次数、天数、秒数共用。 */
export function intIn(text: string, min: number, max: number): number | null {
  const n = Number(text);
  return /^\d+$/.test(text.trim()) && n >= min && n <= max ? n : null;
}

/** 逗号分隔的非负整数列表，例如重试退避 "1000,5000,30000"。 */
export function parseIntList(text: string): number[] | null {
  const parts = text.split(",").map((s) => s.trim()).filter(Boolean);
  const nums = parts.map(Number);
  return nums.every((n) => Number.isInteger(n) && n >= 0) ? nums : null;
}

/** 十进制金额文本转 int64 微单位（硬性规则 6），最多 6 位小数，用整数运算避免浮点误差。 */
export function parseMoney(text: string): number | null {
  const m = /^(-?)(\d+)(?:\.(\d{1,6}))?$/.exec(text.trim());
  if (!m) return null;
  const micro = Number(m[2]) * 1_000_000 + Number((m[3] ?? "").padEnd(6, "0"));
  return Number.isSafeInteger(micro) ? (m[1] ? -micro : micro) : null;
}

/** 微单位转输入框里的十进制文本，去掉末尾的零。 */
export function formatMoneyInput(micro: number): string {
  const abs = Math.abs(micro);
  const frac = String(abs % 1_000_000).padStart(6, "0").replace(/0+$/, "");
  return `${micro < 0 ? "-" : ""}${Math.trunc(abs / 1_000_000)}${frac ? "." + frac : ""}`;
}

/** datetime-local 的值按北京时间解释（D174），转 RFC 3339 UTC（硬性规则 7）。 */
export function fromLocalInput(value: string): string | null {
  const d = new Date(`${value}${TZ_OFFSET}`);
  return value && !Number.isNaN(d.getTime()) ? d.toISOString() : null;
}

/** RFC 3339 转 datetime-local 的值：北京时间，到秒；无效时为空串。 */
export function toLocalInput(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : toBeijing(d).toISOString().slice(0, 19);
}

/** 时:分时段是否合法：起止都是 时:分、不相同；结束早于开始表示跨午夜。路由明细的生效时间与按时间策略的成员共用（决策 D64、D69）。 */
export function validClockRange(start: string, end: string): boolean {
  const clock = /^([01]\d|2[0-3]):[0-5]\d$/;
  return clock.test(start) && clock.test(end) && start !== end;
}

/** 解析 JSON 文本；空文本按 fallback 处理。 */
export function parseJson(text: string, fallback: unknown): unknown | null {
  if (!text.trim()) return fallback;
  try { return JSON.parse(text) as unknown; } catch { return null; }
}

/** UTF-8 字节数。服务端的上限按字节算：bcrypt 只取口令前 72 字节，后台账号用户名不超过 64 字节（决策 D43）。 */
export function utf8Bytes(s: string): number {
  return new TextEncoder().encode(s).length;
}

let lastRowKey = 0;

/** 可增删行编辑器里一行的前端标识（React 的 key）：整个页面生命周期内递增、不重复，新增与载入的行都从这里取。 */
export function rowKey(): number {
  return ++lastRowKey;
}

/** 把第 i 项与相邻项对调（dir 为 -1 上移、1 下移），返回新数组；越界时原样返回。表格里的行排序共用。 */
export function moveItem<T>(list: T[], i: number, dir: -1 | 1): T[] {
  const j = i + dir;
  if (j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

export function formatJson(v: unknown): string {
  return JSON.stringify(v ?? {}, null, 2);
}
